// A súgó-asszisztens widgetjének DOM nélküli magja: az SSE-válasz dekódolása, a
// beszélgetés és a válaszok modellje (a citációs jelölőkkel), a linkek szűrése és a
// munkamenet mentése. A felület az assistant.ts-ben van; a háttér szerződése (események,
// hibakódok) a Web\Api\HelpAssistant\README.md-ben.
//
// A search.ts-hez hasonlóan nincs relatív importja és csak "törölhető" TS-szintaxist
// használ (nincs enum, namespace, paraméter-property), így a Node a tesztekből
// közvetlenül betölti (test/assistant-core.test.mjs).
// Darabokban érkező text/event-stream szöveg → teljes események. Egy esemény akár több
// hálózati darabra is szétszakadhat, és egy darabban több esemény is jöhet.
export class SseDecoder {
    constructor() {
        Object.defineProperty(this, "buffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
    }
    push(chunk) {
        this.buffer += chunk.replace(/\r\n?/g, "\n");
        const messages = [];
        let boundary = this.buffer.indexOf("\n\n");
        while (boundary >= 0) {
            const block = this.buffer.slice(0, boundary);
            this.buffer = this.buffer.slice(boundary + 2);
            const message = parseBlock(block);
            if (message) {
                messages.push(message);
            }
            boundary = this.buffer.indexOf("\n\n");
        }
        return messages;
    }
}
function parseBlock(block) {
    let event = "message";
    const data = [];
    for (const line of block.split("\n")) {
        if (line.startsWith("event:")) {
            event = line.slice(6).trim();
        }
        else if (line.startsWith("data:")) {
            data.push(line.slice(5).replace(/^ /, ""));
        }
    }
    return data.length > 0 ? { event, data: data.join("\n") } : undefined;
}
export function createConversation() {
    return { sources: {}, entries: [] };
}
export function isAnswer(entry) {
    return entry.question !== undefined;
}
export function startAnswer(conversation, question) {
    const answer = { question, markdown: "", cited: [], status: "waiting" };
    conversation.entries.push(answer);
    return answer;
}
// A jelölő a markdownban: magánhasználatú karakterek közé zárt szám, amit se a markdown,
// se a DOMPurify nem bánt; renderelés után a felület cseréli linkre.
const markerStart = "";
const markerEnd = "";
export function citationMarker(number) {
    return `${markerStart}${number}${markerEnd}`;
}
// Egy szöveg darabjai: a sima szöveg string, a jelölő helyén a citáció száma.
export function splitCitations(text) {
    const parts = [];
    let last = 0;
    // Minden hívás saját regexet kap: egy megosztott, globális regex lastIndex-e a hívások
    // között megmaradna.
    for (const match of text.matchAll(/(\d+)/g)) {
        const index = match.index ?? 0;
        if (index > last) {
            parts.push(text.slice(last, index));
        }
        parts.push(Number(match[1]));
        last = index + match[0].length;
    }
    if (last < text.length) {
        parts.push(text.slice(last));
    }
    return parts;
}
// Egy SSE-esemény alkalmazása a beszélgetésre és az épp futó válaszra. Ismeretlen
// eseményt és hibás adatot figyelmen kívül hagy (a háttér újabb verziója hozhat újat).
export function applyEvent(conversation, answer, message) {
    let data;
    try {
        data = JSON.parse(message.data);
    }
    catch {
        return;
    }
    const record = isRecord(data) ? data : undefined;
    switch (message.event) {
        case "start":
            if (record && typeof record.conversationId === "string") {
                conversation.id = record.conversationId;
            }
            if (record && typeof record.turn === "number") {
                answer.turn = record.turn;
            }
            break;
        case "sources":
            if (Array.isArray(data)) {
                for (const source of data) {
                    if (isSource(source)) {
                        conversation.sources[source.id] = source;
                    }
                }
            }
            break;
        case "text":
            if (record && typeof record.delta === "string") {
                answer.markdown += record.delta;
                answer.status = "streaming";
            }
            break;
        case "cite":
            if (record && typeof record.id === "number" && conversation.sources[record.id]) {
                addCitation(answer, record.id);
            }
            break;
        case "done":
            answer.status = "done";
            answer.stopReason = record && typeof record.stopReason === "string" ? record.stopReason : undefined;
            break;
        case "error":
            answer.status = "error";
            answer.error = {
                code: record && typeof record.code === "string" ? record.code : "upstream",
                message: record && typeof record.message === "string" ? record.message : "A válasz most nem érkezett meg.",
            };
            break;
    }
}
// A jelölő a hivatkozó szöveg után, de a záró sortörés(ek) és szóközök elé kerül —
// különben a következő bekezdés elejére csúszna. Ugyanarra a forrásra közvetlenül
// egymás után nem kerül két jelölő.
function addCitation(answer, sourceId) {
    let number = answer.cited.indexOf(sourceId) + 1;
    if (number === 0) {
        answer.cited.push(sourceId);
        number = answer.cited.length;
    }
    const marker = citationMarker(number);
    const body = answer.markdown.replace(/\s+$/, "");
    const tail = answer.markdown.slice(body.length);
    if (body.endsWith(marker)) {
        return;
    }
    answer.markdown = `${body}${marker}${tail}`;
}
// A stream vége előtt megszakadt válasz (hálózat, a látogató új beszélgetést kezdett).
export function failAnswer(answer, error) {
    answer.status = "error";
    answer.error = error;
}
// A hivatkozott források a megjelenítési számukkal.
export function citedSources(conversation, answer) {
    return answer.cited
        .map((id, index) => ({ number: index + 1, source: conversation.sources[id] }))
        .filter((entry) => entry.source !== undefined);
}
export function sourceLabel(source) {
    return [...source.crumbs, source.title, ...(source.heading ? [source.heading] : [])].join(" › ");
}
// -------------------------------------------------------- HTTP-hibák --
// A stream megnyitása előtti hibaválasz ({ok:false, code, error} vagy {ok:false, errors})
// a látogatónak szánt szöveggé.
export function describeHttpError(status, body) {
    const record = isRecord(body) ? body : {};
    const code = typeof record.code === "string" ? record.code : status === 429 ? "rate_limited" : `http_${status}`;
    if (typeof record.error === "string" && record.error.trim() !== "") {
        return { code, message: record.error };
    }
    if (isRecord(record.errors)) {
        const messages = Object.values(record.errors).filter((value) => typeof value === "string");
        if (messages.length > 0) {
            return { code: "invalid", message: messages.join(" ") };
        }
    }
    return {
        code,
        message: status === 429
            ? "Túl sok kérdés rövid idő alatt. Kérem, próbálja újra később."
            : "Az asszisztens jelenleg nem érhető el. Kérem, próbálja újra később.",
    };
}
// Melyik link maradhat meg a válaszban: a súgó saját lapjai (relatív, vagy a lap saját
// címe) a felületen belül nyílnak, a www.cobra.hu új lapon; minden más link szöveggé
// válik. A modell a rendszerprompt szerint nem ír linket, de ezt nem lehet kikényszeríteni.
export function classifyLink(href, pageOrigin) {
    if (!href) {
        return undefined;
    }
    let url;
    try {
        url = new URL(href, `${pageOrigin}/`);
    }
    catch {
        return undefined;
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") {
        return undefined;
    }
    if (url.origin === pageOrigin || url.origin === "https://help.cobra.hu") {
        return "internal";
    }
    return url.origin === "https://www.cobra.hu" ? "external" : undefined;
}
// A súgó egy lapjának címe (/?product=…&version=…&page=…#horgony) a felület
// navigációjához; a horgony dekódolva.
export function parseHelpRoute(href, pageOrigin) {
    let url;
    try {
        url = new URL(href, `${pageOrigin}/`);
    }
    catch {
        return undefined;
    }
    const page = url.searchParams.get("page") ?? undefined;
    if (!page) {
        return undefined;
    }
    let anchor;
    if (url.hash.length > 1) {
        try {
            anchor = decodeURIComponent(url.hash.slice(1));
        }
        catch {
            anchor = url.hash.slice(1);
        }
    }
    return {
        product: url.searchParams.get("product") ?? undefined,
        version: url.searchParams.get("version") ?? undefined,
        page,
        anchor,
    };
}
// ------------------------------------------------------------ mentés --
// A munkamenet (sessionStorage) tartalma: egy oldalfrissítés után a beszélgetés
// folytatható. A félbemaradt válasz hibaként jön vissza (a stream nem folytatható).
const storageFormat = 1;
const maxStoredEntries = 40;
export function serializeConversation(conversation) {
    return JSON.stringify({
        format: storageFormat,
        id: conversation.id,
        productId: conversation.productId,
        sources: conversation.sources,
        entries: conversation.entries.slice(-maxStoredEntries),
    });
}
export function restoreConversation(text) {
    if (!text) {
        return undefined;
    }
    let data;
    try {
        data = JSON.parse(text);
    }
    catch {
        return undefined;
    }
    if (!isRecord(data) || data.format !== storageFormat || !isRecord(data.sources) || !Array.isArray(data.entries)) {
        return undefined;
    }
    const conversation = createConversation();
    conversation.id = typeof data.id === "string" ? data.id : undefined;
    conversation.productId = typeof data.productId === "string" ? data.productId : undefined;
    for (const source of Object.values(data.sources)) {
        if (isSource(source)) {
            conversation.sources[source.id] = source;
        }
    }
    for (const entry of data.entries) {
        if (isRecord(entry) && typeof entry.note === "string") {
            conversation.entries.push({ note: entry.note });
        }
        else if (isRecord(entry) && typeof entry.question === "string" && typeof entry.markdown === "string" && Array.isArray(entry.cited)) {
            const answer = {
                question: entry.question,
                turn: typeof entry.turn === "number" ? entry.turn : undefined,
                markdown: entry.markdown,
                cited: entry.cited.filter((id) => typeof id === "number"),
                status: entry.status === "done" || entry.status === "error" ? entry.status : "error",
                stopReason: typeof entry.stopReason === "string" ? entry.stopReason : undefined,
                rating: entry.rating === "up" || entry.rating === "down" ? entry.rating : undefined,
            };
            if (answer.status === "error") {
                answer.error = isRecord(entry.error) && typeof entry.error.message === "string" && typeof entry.error.code === "string"
                    ? { code: entry.error.code, message: entry.error.message }
                    : { code: "interrupted", message: "A válasz megszakadt." };
            }
            conversation.entries.push(answer);
        }
    }
    return conversation;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isSource(value) {
    return isRecord(value)
        && typeof value.id === "number"
        && typeof value.title === "string"
        && Array.isArray(value.crumbs) && value.crumbs.every((crumb) => typeof crumb === "string")
        && typeof value.heading === "string"
        && typeof value.href === "string";
}
